/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2024.
 */

import { DeliveryPickupDates } from '@/components/blocks/DeliveryPickupDates';
import { HDMPriceDisplay } from '@/components/blocks/HDMPriceDisplay';
import { Linkable } from '@/components/blocks/Linkable';
import { MuiCardMedia } from '@/components/blocks/MuiCardMedia';
import { useIsStoreOnlyAvailable } from '@/components/blocks/PLPProductCard/hooks/useIsStoreOnlyAvailable';
import { ProductSameDayTag } from '@/components/blocks/PLPProductCard/parts/ProductSameDayTag';
import { reservedSlotSx, spacerSx } from '@/components/blocks/PLPProductCard/styles/plpProductCard';
import { ProductCardAddToCartWithInventory } from '@/components/blocks/ProductCard/parts/AddToCartWithInventory';
import { ProductCardChooseOptions } from '@/components/blocks/ProductCard/parts/ChooseOptions';
import { PromoBadges } from '@/components/blocks/ProductCard/parts/PromoBadges';
import { brandSX } from '@/components/blocks/ProductCard/styles/brand';
import { productCardSX } from '@/components/blocks/ProductCard/styles/card';
import { productCardContentSX } from '@/components/blocks/ProductCard/styles/cardContent';
import { productCardMediaSX } from '@/components/blocks/ProductCard/styles/cardMedia';
import { productCardDetailsSX } from '@/components/blocks/ProductCard/styles/details';
import { productCardNameSX } from '@/components/blocks/ProductCard/styles/name';
import {
	bundlePriceContainerSx,
	bundlePriceSx,
	productPriceSx,
} from '@/components/blocks/ProductCard/styles/price';
import { skuBoxSx } from '@/components/blocks/ProductCard/styles/productBox';
import { shortDescriptionSX } from '@/components/blocks/ProductCard/styles/shortDescription';
import { superSkuBoxSX, superSkuLinkSX } from '@/components/blocks/ProductCard/styles/superSku';
import { Box, Card, CardContent, Stack, SxProps, Theme, Typography } from '@mui/material';
import { FC, useContext, useMemo } from 'react';

import { MultiFreeGiftPromoContext } from '@/components/blocks/PLPProductCard/context/MultiFreeGiftPromoContext';
import { RatingCard } from '@/components/blocks/RatingCard';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useIsEddPlpEnabled } from '@/data/config/eddPlpToggle';
import { useCatalogEntryList } from '@/data/Content/CatalogEntryList';
import { useHomeDeliveryDate } from '@/data/Content/HomeDeliveryDate';
import { useStoreIDS } from '@/data/state/useStoreIDs';
import {
	getMultiFreeGiftPromoCode,
	useMultiFreeGiftPromoDetails,
} from '@/data/Content/MultiFreeGiftPromoDetails';
import { useProductCard } from '@/data/Content/_ProductCard';
import { useProductEvents } from '@/data/Content/_ProductEvents';
import { useLocalization } from '@/data/Localization';
import { COOKIES } from '@/data/constants/cookie';
import { TYPES } from '@/data/constants/product';
import { ContentContext, ContentProvider } from '@/data/context/content';
import { CookiesSingletonContext } from '@/data/cookie/cookiesSingletonProvider';
import { HCLBreadcrumb } from '@/data/types/Breadcrumb';
import { ProductDisplayPrice, ProductType } from '@/data/types/Product';
import { ProductCardContextValue } from '@/data/types/ProductCard';
import { useHasFreeGiftPromoIcon } from '@/data/utils/usePromoIcons';
import { getHref_Product } from '@/utils/getHref_Product';
import { replaceImagePath } from '@/utils/hdmPathConverter';
import { mergeSx } from '@/utils/mergeSx';
import { parseHTML } from '@/utils/parseHTMLModern';
import { productIsA } from '@/utils/productIsA';
import { useTrackSelectItem } from 'presentation/events/hooks/useTrackSelectItem';
import { CDN_BASE_URL, OpenSansFontWeights } from 'shared/custom/constants/commonConstants';
import { useGetCategories } from 'shared/custom/hooks/Categories';
import { findParentCategories } from 'shared/custom/utils/getCategoryByIdForGA';

export const ProductCard: FC<{
	product: ProductType;
	parentCrumb?: HCLBreadcrumb[];
	clickAction?: () => void;
	showInventory?: boolean;
	isEmarsys?: boolean;
	productCardSx?: SxProps<Theme>;
	// eslint-disable-next-line complexity
}> = ({ product, clickAction, parentCrumb, showInventory, isEmarsys, ...props }) => {
	const parentCtxValue = useContext(ContentContext) as ReturnType<typeof useCatalogEntryList>;
	const { getCookie } = useContext(CookiesSingletonContext);
	const trail = useMemo(() => getCookie(COOKIES.breadcrumb), [getCookie]);
	const eventProps = useMemo(() => ({ product }), [product]);
	const priceDisplayNLS = useLocalization('PriceDisplay');
	const productListNLS = useLocalization('ProductList');
	const productCardValue = useProductCard(product, true);
	const { sku } = productCardValue;
	const { onClick } = useProductEvents(eventProps);
	const routeUrl = useMemo(
		() => getHref_Product(product, parentCrumb, trail as string[]),
		[parentCrumb, product, trail]
	);
	const contextValue: ProductCardContextValue = useMemo(
		() => ({ ...parentCtxValue, ...productCardValue, product, routeUrl }),
		[parentCtxValue, product, productCardValue, routeUrl]
	);
	const shouldShowInventory =
		product.items || productIsA(product, TYPES.kit) || productIsA(product, TYPES.sku);
	const trackSelectItem = useTrackSelectItem();
	const { navigation } = useGetCategories();
	const categories = useMemo(() => {
		if (!product?.parentCatalogGroupID) return [];
		return findParentCategories(navigation, product.parentCatalogGroupID);
	}, [navigation, product?.parentCatalogGroupID]);
	const isSuperSKU = product['identifier.sku.parent']?.endsWith('-ss');
	const isBundle = productIsA(product, TYPES.bundle);
	const isStoreOnlyAvailable = useIsStoreOnlyAvailable({ product });
	const isEddPlpEnabled = useIsEddPlpEnabled();
	const showsDeliveryDates = isEddPlpEnabled && !isStoreOnlyAvailable && !isBundle;
	const { physicalStoreId } = useStoreIDS();
	const homeDelivery = useHomeDeliveryDate(
		showsDeliveryDates ? sku ?? product : undefined,
		physicalStoreId
	);
	const isSameDay = homeDelivery.status === 'same-day';

	const batchedPromoMap = useContext(MultiFreeGiftPromoContext);
	const isBatched = batchedPromoMap !== null;

	const hasFreeGiftIcon = useHasFreeGiftPromoIcon(product);
	const { data: multiFreeGiftPromo } = useMultiFreeGiftPromoDetails(
		!isBatched && hasFreeGiftIcon ? product.id : ''
	);
	// Free-gift variant (PGC/PGNC/TNC-PGC/TNC-PGNC/PG) for the select_item promo_thd.
	const promoThdCode = isBatched
		? batchedPromoMap?.get(product.id)
		: getMultiFreeGiftPromoCode(multiFreeGiftPromo, product.id);

	const thumbnailImg = useMemo(() => {
		const cleanPartNumber = product.partNumber.replace(/[PB]/g, '');
		const defaultProductThumbnail = `${CDN_BASE_URL}/productos/${cleanPartNumber}/${cleanPartNumber}.jpg`;

		const rawAttachmentUrls = product?.['attachments.url'];
		const attachmentUrls = Array.isArray(rawAttachmentUrls)
			? rawAttachmentUrls
			: rawAttachmentUrls
				? [rawAttachmentUrls]
				: [];

		if (isBundle) {
			const imgPath = attachmentUrls.find((url) => url && !url.includes('-'));
			return imgPath || `${CDN_BASE_URL}/assets/img/imagen-no-disponible.svg`;
		}

		const baseImg = (sku ?? product).thumbnail;

		if (!baseImg) {
			const urlFromList = attachmentUrls.find(
				(url) => url && !url.includes('-') && url.includes('.jpg')
			);
			const assetPath = product.attachments?.find(
				(img) => img?.attachmentAssetPath && !img.attachmentAssetPath.includes('-')
			)?.attachmentAssetPath;

			const imgPath = urlFromList || assetPath;
			return imgPath ? replaceImagePath(imgPath) : defaultProductThumbnail;
		}

		return replaceImagePath(baseImg);
	}, [product, isBundle, sku]);

	const bundlePriceRange: ProductDisplayPrice | null = useMemo(() => {
		if (!isBundle || !product.components || product.components.length === 0) {
			return null;
		}

		const prices = product.components
			.map((c) => {
				// to-do: match react logic for now. Need to revisit
				const val0 = c.price?.[0]?.value;
				const val1 = c.price?.[1]?.value;

				const rawValue = val0 || val1;
				return rawValue !== undefined ? Number(rawValue) : null;
			})
			.filter((p): p is number => typeof p === 'number');

		if (prices.length === 0) return null;

		return {
			min: Math.min(...prices),
			max: prices.reduce((acc, curr) => acc + curr, 0),
			currency: product.components[0].productPrice.currency,
		};
	}, [product, isBundle]);

	const handleTrackSelectItem = () => {
		if (product) {
			trackSelectItem({
				product: { ...product, promoThdCode },
				isEmarsys,
				categories,
			});
		}
	};

	return (
		// eslint-disable-next-line react/forbid-component-props
		<Box
			id={`recommendProd_${product.partNumber}`}
			className="scarab-item"
			data-scarabitem={product.id}
		>
			{/* eslint-disable-next-line react/forbid-component-props */}
			<Linkable
				href={routeUrl}
				color="textPrimary"
				onClick={handleTrackSelectItem}
				className="product-sku"
			>
				<Typography variant="body1" sx={skuBoxSx}>{`SKU ${product.partNumber}`}</Typography>
			</Linkable>
			<Card
				onClick={onClick(clickAction)}
				sx={mergeSx(productCardSX, props.productCardSx)}
				data-testid={product.partNumber}
			>
				{/* eslint-disable-next-line react/forbid-component-props */}
				<Box sx={productCardDetailsSX} className="product-image">
					<Linkable href={routeUrl} color="textPrimary" onClick={handleTrackSelectItem}>
						<MuiCardMedia
							sx={productCardMediaSX}
							component="img"
							image={thumbnailImg}
							title={product.name}
						></MuiCardMedia>
					</Linkable>
				</Box>
				<CardContent sx={productCardContentSX}>
					<Stack>
						{!isBundle ? (
							// eslint-disable-next-line react/forbid-component-props
							<Typography
								variant="bodyBaseline"
								align="left"
								sx={brandSX}
								id={`${product.partNumber}-brand`}
								data-testid={`${product.partNumber}-brand`}
								className="product-brand"
							>
								<Linkable href={routeUrl} color="textPrimary" onClick={handleTrackSelectItem}>
									{product?.manufacturer && parseHTML(product.manufacturer?.toLowerCase())}
								</Linkable>
							</Typography>
						) : null}
						{/* eslint-disable-next-line react/forbid-component-props */}
						<Typography
							variant="bodyBaseline"
							align="left"
							sx={isBundle ? brandSX : productCardNameSX}
							id={`${product.partNumber}-name`}
							data-testid={`${product.partNumber}-name`}
							className="product-name"
						>
							<Linkable href={routeUrl} color="textPrimary" onClick={handleTrackSelectItem}>
								{product?.name && parseHTML(product.name?.toLowerCase())}
							</Linkable>
						</Typography>
						<Box>
							<Stack direction={'row'}>
								{isBundle && bundlePriceRange ? (
									<Box sx={bundlePriceSx}>
										<HDMPriceDisplay
											price={bundlePriceRange}
											type="simple"
											showRange={true}
											sx={bundlePriceContainerSx}
										/>
										<PromoBadges product={product} />
									</Box>
								) : product.productPrice.offer ? (
									// eslint-disable-next-line react/forbid-component-props
									<Box sx={productPriceSx} className="product-price">
										<HDMPriceDisplay
											price={product?.productPrice}
											type="simple"
											sx={{ mb: '15px' }}
										/>
										<PromoBadges product={product} />
									</Box>
								) : (
									<Typography variant="body1" align="left" sx={{ mt: 1, mb: 4 }}>
										{priceDisplayNLS.Labels.Pending.t()}
									</Typography>
								)}
							</Stack>
						</Box>

						{isBundle ? (
							<Box sx={shortDescriptionSX}>
								<Typography variant="bodyBaseline" fontWeight={OpenSansFontWeights.regular}>
									{product.shortDescription ?? ''}
								</Typography>
							</Box>
						) : null}

						{!isBundle ? (
							<Box>
								<Linkable href={routeUrl} onClick={handleTrackSelectItem}>
									<RatingCard ratings={product.ratings} />
								</Linkable>
							</Box>
						) : null}
						{isSuperSKU ? (
							<Box sx={superSkuBoxSX}>
								<Linkable
									href={routeUrl}
									color="secondary"
									onClick={handleTrackSelectItem}
									sx={superSkuLinkSX}
								>
									<DynamicIcon name="stacks" variant="rounded" />
									<Typography variant="bodyBaseline" color="secondary">
										{productListNLS.MoreOptionsAvailable.t()}
									</Typography>
								</Linkable>
							</Box>
						) : null}

						{/* EDD + Same Day tag — mirrors PLPProductCard (story 163212). */}

						{!isEddPlpEnabled || isBundle ? <Box sx={spacerSx} /> : null}
						{isEddPlpEnabled &&
							(showsDeliveryDates ? (
								<DeliveryPickupDates product={sku ?? product} />
							) : (
								<Box sx={reservedSlotSx(37, 60)} />
							))}

						{isSameDay ? (
							<Box py={'10px'}>
								<ProductSameDayTag />
							</Box>
						) : null}
					</Stack>
				</CardContent>
				<Box>
					{showInventory ? (
						<ContentProvider value={contextValue}>
							{shouldShowInventory ? (
								<ProductCardAddToCartWithInventory />
							) : (
								<ProductCardChooseOptions onClickProduct={handleTrackSelectItem} />
							)}
						</ContentProvider>
					) : null}
				</Box>
			</Card>
		</Box>
	);
};
